import { shortHash } from './habits'

// Revision passes: named setups of Editor Mode, worked through in a
// suggested order (big things first, so no one polishes a sentence they will
// later cut). A pass is a name, a brief that says what to look for, and the
// lenses, tab, view and page dress it opens with. The plan (which passes, in
// what order) is global and lives in settings.editorMode.passes; how far each
// page has got lives in the revision store.

export type LensKey =
  | 'sentenceLines'
  | 'sentenceCounts'
  | 'openers'
  | 'echoes'
  | 'filterWords'
  | 'crutchWords'
  | 'dialogueTags'
  | 'paragraphWalls'

export const LENS_KEYS: LensKey[] = [
  'sentenceLines',
  'sentenceCounts',
  'openers',
  'echoes',
  'filterWords',
  'crutchWords',
  'dialogueTags',
  'paragraphWalls',
]

export type EditorModeTab = 'openers' | 'starts' | 'rhythm' | 'habits' | 'dialogue'
const EDITOR_MODE_TABS: EditorModeTab[] = ['openers', 'starts', 'rhythm', 'habits', 'dialogue']

export type LensSet = Record<LensKey, boolean>

export interface PassSetup {
  view: 'page' | 'screenplay'
  /** Re-dress the page (Editor Mode's face and colors); off keeps the drafting look. */
  dress: boolean
  /** The analysis tab the pass opens on; null leaves it where it is. */
  tab: EditorModeTab | null
  lenses: LensSet
  /** Run every proofing check while the pass is on, whatever the proofing settings say. */
  proofing: boolean
}

export interface RevisionPass {
  id: string
  builtIn: boolean
  name: string
  brief: string
  hidden: boolean
  setup: PassSetup
}

export const PASS_NAME_MAX = 32
export const PASS_BRIEF_MAX = 240
const MAX_PASSES = 24

function noLenses(): LensSet {
  return Object.fromEntries(LENS_KEYS.map((key) => [key, false])) as LensSet
}

function lenses(...on: LensKey[]): LensSet {
  const set = noLenses()
  for (const key of on) set[key] = true
  return set
}

function builtIn(id: string, name: string, brief: string, setup: Partial<PassSetup>): RevisionPass {
  return {
    id,
    builtIn: true,
    name,
    brief,
    hidden: false,
    setup: { view: 'page', dress: true, tab: null, lenses: noLenses(), proofing: false, ...setup },
  }
}

/** The default plan, in order. Ids are stable: progress is stored against them. */
export const BUILT_IN_PASSES: readonly RevisionPass[] = [
  builtIn('fresh-read', 'Fresh read', 'Read it as a stranger would. Flag what snags as a loose end and keep going; fix it in a later pass.', {}),
  builtIn('rhythm', 'Rhythm', 'Break up runs of same-length sentences and walls of text. Let a short sentence land.', {
    lenses: lenses('sentenceLines', 'sentenceCounts', 'paragraphWalls'),
    tab: 'rhythm',
  }),
  builtIn('openers', 'Openers', 'Vary how sentences begin: the "He… He… As…" habits and back-to-back repeats.', {
    lenses: lenses('openers'),
    tab: 'openers',
  }),
  builtIn('habits', 'Habits', 'Cut or sharpen the words you lean on: echoes, filter words, crutch words.', {
    lenses: lenses('echoes', 'filterWords', 'crutchWords'),
    tab: 'habits',
  }),
  builtIn('dialogue', 'Dialogue', 'Can you always tell who is speaking? Does each voice sound like itself? Let "said" do its job.', {
    lenses: lenses('dialogueTags'),
    tab: 'dialogue',
  }),
  builtIn('proof', 'Proof', 'Last of all: spelling, grammar and punctuation. Every proofing check is on for this pass only.', {
    proofing: true,
  }),
]

function builtInPass(id: string): RevisionPass | undefined {
  const pass = BUILT_IN_PASSES.find((p) => p.id === id)
  return pass ? clonePass(pass) : undefined
}

function clonePass(pass: RevisionPass): RevisionPass {
  return { ...pass, setup: { ...pass.setup, lenses: { ...pass.setup.lenses } } }
}

export function defaultPasses(): RevisionPass[] {
  return BUILT_IN_PASSES.map(clonePass)
}

/** A built-in pass as it shipped, keeping where it sits and whether it is hidden. */
export function resetPass(pass: RevisionPass): RevisionPass {
  const original = builtInPass(pass.id)
  return original ? { ...original, hidden: pass.hidden } : pass
}

function newPassId(): string {
  return `custom-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`
}

/** A new custom pass, blank or copied from another. */
export function newCustomPass(from?: RevisionPass): RevisionPass {
  const base = from ? clonePass(from) : builtIn('', 'New pass', '', {})
  return {
    ...base,
    id: newPassId(),
    builtIn: false,
    name: from ? `${from.name} (copy)`.slice(0, PASS_NAME_MAX) : 'New pass',
    hidden: false,
  }
}

function normalizeSetup(raw: any, fallback: PassSetup): PassSetup {
  const lensSet = noLenses()
  for (const key of LENS_KEYS) lensSet[key] = typeof raw?.lenses?.[key] === 'boolean' ? raw.lenses[key] : fallback.lenses[key]
  return {
    view: raw?.view === 'screenplay' ? 'screenplay' : raw?.view === 'page' ? 'page' : fallback.view,
    dress: typeof raw?.dress === 'boolean' ? raw.dress : fallback.dress,
    tab: raw?.tab === null ? null : EDITOR_MODE_TABS.includes(raw?.tab) ? raw.tab : fallback.tab,
    lenses: lensSet,
    proofing: typeof raw?.proofing === 'boolean' ? raw.proofing : fallback.proofing,
  }
}

/**
 * Cleans a stored plan. Built-in passes can be hidden but never deleted, so
 * any missing from the stored list are added back at the end.
 */
export function normalizePasses(raw: unknown): RevisionPass[] {
  if (!Array.isArray(raw)) return defaultPasses()
  const seen = new Set<string>()
  const passes: RevisionPass[] = []
  for (const item of raw) {
    const id = typeof item?.id === 'string' ? item.id.slice(0, 40) : ''
    if (!id || seen.has(id)) continue
    const original = builtInPass(id)
    if (!original && !id.startsWith('custom-')) continue
    seen.add(id)
    const fallback = original ?? builtIn(id, 'Pass', '', {})
    const name = typeof item.name === 'string' && item.name.trim() ? item.name.trim().slice(0, PASS_NAME_MAX) : fallback.name
    passes.push({
      id,
      builtIn: !!original,
      name,
      brief: typeof item.brief === 'string' ? item.brief.slice(0, PASS_BRIEF_MAX) : fallback.brief,
      hidden: item.hidden === true,
      setup: normalizeSetup(item.setup, fallback.setup),
    })
    if (passes.length >= MAX_PASSES) break
  }
  for (const pass of BUILT_IN_PASSES) if (!seen.has(pass.id)) passes.push(clonePass(pass))
  return passes
}

// ── "Changed since" fingerprints ─────────────────────────────────────────
// A finished pass remembers a small sketch of the page's paragraphs (the
// smallest hashes, a MinHash bottom-k sketch) rather than the text, so the
// synced row stays tiny. Comparing two sketches estimates how many
// paragraphs are new or rewritten.

const SKETCH_SIZE = 32

function paragraphHash(text: string): number {
  return parseInt(shortHash(text.replace(/\s+/g, ' ').trim()), 36)
}

export function paragraphSketch(paragraphs: string[]): number[] {
  const hashes = new Set<number>()
  for (const text of paragraphs) if (text.trim()) hashes.add(paragraphHash(text))
  return [...hashes].sort((a, b) => a - b).slice(0, SKETCH_SIZE)
}

/**
 * Estimated share (0–1) of the page's paragraphs that are new or rewritten
 * between two sketches. With m of n paragraphs changed, the sketches' Jaccard
 * distance d is 2m / (n + m), so the share is d / (2 − d).
 */
export function sketchChangedShare(before: number[], after: number[]): number {
  if (!before.length && !after.length) return 0
  if (!before.length || !after.length) return 1
  const a = new Set(before)
  const b = new Set(after)
  const union = [...new Set([...before, ...after])].sort((x, y) => x - y).slice(0, SKETCH_SIZE)
  const shared = union.filter((h) => a.has(h) && b.has(h)).length
  const distance = 1 - shared / union.length
  return distance / (2 - distance)
}
